import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon, FlagIcon } from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { Button } from "components/ui";
import { ConfirmModal } from "components/shared/ConfirmModal";
import {
  getInteractionLevelList,
  setLeadInteractionLevel,
} from "services/leadsService";

const SetInteractionLevelModal = ({
  isOpen,
  onClose,
  leadData,
  handleInteractionLevelChange,
}) => {
  const [selectedInteractionLevel, setSelectedInteractionLevel] =
    useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [interactionLevels, setInteractionLevels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmState, setConfirmState] = useState("pending");
  const [confirmMessages, setConfirmMessages] = useState({});
  const saveRef = useRef(null);

  // Fetch priority list when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchInteractionLevels();
    } else {
      // Reset confirmation modal state when main modal closes
      setShowConfirmModal(false);
      setConfirmState("pending");
      setIsSubmitting(false);
    }
  }, [isOpen]);
  // Set initial interaction level based on lead data
  useEffect(() => {
    if (leadData?.InteractionLevelName && interactionLevels.length > 0) {
      const currentInteractionLevel = interactionLevels.find(
        (i) =>
          i.InteractionLevelName.toLowerCase() ===
          leadData.InteractionLevelName.toLowerCase(),
      );
      if (currentInteractionLevel) {
        setSelectedInteractionLevel(currentInteractionLevel.Id);
      } else {
        // Default to first interaction level if current one not found
        setSelectedInteractionLevel(interactionLevels[0]?.Id);
      }
    } else if (interactionLevels.length > 0 && !selectedInteractionLevel) {
      setSelectedInteractionLevel(interactionLevels[0]?.Id);
    }
  }, [leadData?.InteractionLevelName, interactionLevels]);
  const fetchInteractionLevels = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getInteractionLevelList();
      if (result.success) {
        setInteractionLevels(result.data);
      } else {
        setError(result.error || "Failed to load interaction levels");
      }
    } catch (err) {
      setError("Failed to load interaction levels");
      console.error("Error fetching interaction levels:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    if (!selectedInteractionLevel) return;

    const selectedInteractionLevelData = interactionLevels.find(
      (i) => i.Id === selectedInteractionLevel,
    );

    setConfirmMessages({
      pending: {
        title: "Confirm Interaction Level Change",
        description: `Are you sure you want to change the interaction level of ${leadData?.name || "this lead"} to ${selectedInteractionLevelData?.InteractionLevelName || "this interaction level"}?`,
        actionText: "Confirm",
      },
      success: {
        title: "Interaction Level Updated",
        description:
          "The lead interaction level has been successfully updated.",
        actionText: "Done",
      },
      error: {
        title: "Update Failed",
        description: "Failed to update interaction level. Please try again.",
        actionText: "Retry",
      },
    });

    setConfirmState("pending");
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Use the new setLeadInteractionLevel service
      const result = await setLeadInteractionLevel(
        leadData?.id,
        selectedInteractionLevel,
      );
      if (result?.success) {
        setConfirmState("success");
        // Call the onInteractionLevelChange callback with the updated lead data
        handleInteractionLevelChange();
      } else {
        setConfirmState("error");
        setError(result.error || "Failed to set interaction level");
      }
    } catch (error) {
      console.error("Error setting interaction level:", error);
      setConfirmState("error");
      setError("Failed to set interaction level. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmModal(false);
    if (confirmState === "success") {
      onClose();
    }
  };
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={()=>{}}
        initialFocus={saveRef}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="dark:bg-dark-700 relative flex w-full max-w-lg origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 outline-none focus:outline-none">
            {/* Header */}
            <div className="dark:bg-dark-800 flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 sm:px-5">
              <DialogTitle
                as="h3"
                className="dark:text-dark-100 flex items-center gap-2 text-base font-medium text-gray-800"
              >
                <FlagIcon className="h-5 w-5 text-orange-600" />
                Interaction Level
              </DialogTitle>
              <Button
                onClick={onClose}
                variant="flat"
                isIcon
                className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
              >
                <XMarkIcon className="size-4.5" />
              </Button>
            </div>

            {/* Body (scrollable) */}
            <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">
              {error && (
                <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
                  {error}
                </div>
              )}

              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                Set the interaction level for {leadData?.name || "this lead"} to
                help organize and prioritize follow-ups.
              </p>

              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                    Loading interaction levels...
                  </span>
                </div>
              ) : interactionLevels.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  No interaction levels available
                </div>
              ) : (
                <div className="space-y-3">
                  {interactionLevels.map((interactionLevel) => (
                    <label
                      key={interactionLevel.Id}
                      className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
                    >
                      <input
                        type="radio"
                        name="interactionLevel"
                        value={interactionLevel.Id}
                        checked={
                          selectedInteractionLevel === interactionLevel.Id
                        }
                        onChange={(e) =>{
                          setSelectedInteractionLevel(Number(e.target.value))

                        }
                        }
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <span className={`text-sm font-medium`}>
                          {interactionLevel.InteractionLevelName}
                        </span>
                        {interactionLevel.Description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {interactionLevel.Description}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Footer (fixed) */}
            <div className="dark:bg-dark-700 flex justify-end gap-3 border-t border-gray-200 bg-white px-4 py-3 sm:px-5 dark:border-gray-700">
              <Button
                onClick={onClose}
                variant="outlined"
                className="min-w-[7rem] rounded-full"
                disabled={isSubmitting || isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                color="primary"
                ref={saveRef}
                className="min-w-[7rem] rounded-full"
                disabled={
                  isSubmitting || isLoading || !selectedInteractionLevel
                }
              >
                {isSubmitting ? "Setting..." : "Set Interaction Level"}
              </Button>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>

      {/* Confirmation Modal */}
      <ConfirmModal
        show={showConfirmModal}
        onClose={handleConfirmClose}
        onOk={
          confirmState === "success" ? handleConfirmClose : handleConfirmSubmit
        }
        confirmLoading={isSubmitting}
        state={confirmState}
        messages={confirmMessages}
        buttonColor="primary"
      />
    </Transition>
  );
};

export default SetInteractionLevelModal;
